import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Package, Truck, Eye } from "lucide-react";
import SLABadge from "../components/SLABadge";
import api from "../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  JHB_DEPOT: "bg-cyan-50 text-cyan-700 border-cyan-200",
  DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => { loadJobs(); }, []);

  useEffect(() => {
    const interval = setInterval(loadJobs, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadJobs = async () => {
    try {
      const { data } = await api.get("/job-cards");
      setJobs(data.items || []);
    } catch { toast.error("Failed to load jobs"); }
    setLoading(false);
  };

  const handleDeliver = async (jobId) => {
    setActionLoading((p) => ({ ...p, [jobId]: true }));
    try {
      await api.put(`/job-cards/${jobId}/deliver`);
      toast.success("Job marked as delivered!");
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
    setActionLoading((p) => ({ ...p, [jobId]: false }));
  };

  if (loading) return <div className="text-slate-500 p-8">Loading...</div>;

  const depotJobs = jobs.filter((j) => j.status === "JHB_DEPOT");
  const deliveredJobs = jobs.filter((j) => j.status === "DELIVERED");

  return (
    <div data-testid="staff-dashboard" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
          <Package size={24} className="text-teal-600" /> Depot & Delivery
        </h1>
        <p className="text-slate-500 text-sm mt-1">Collect from depot and deliver to Skyworth</p>
      </div>

      {/* Depot Jobs */}
      <div>
        <h2 className="font-heading font-bold text-sm uppercase tracking-[0.15em] text-slate-700 mb-3 flex items-center gap-2">
          <Package size={16} className="text-cyan-400" /> At JHB Depot ({depotJobs.length})
        </h2>
        {depotJobs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400 text-sm">
            No jobs at depot
          </div>
        ) : (
          <div className="space-y-2">
            {depotJobs.map((job) => (
              <div key={job.id} data-testid={`depot-job-${job.id}`}
                className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold text-slate-800">{job.job_card_number}</span>
                    <Badge variant="outline" className={STATUS_COLORS.JHB_DEPOT}>JHB DEPOT</Badge>
                    <SLABadge sla={job.sla} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {job.model} | {job.store} | {job.area}
                  </div>
                </div>
                <Button
                  data-testid={`deliver-btn-${job.id}`}
                  onClick={() => handleDeliver(job.id)}
                  disabled={actionLoading[job.id]}
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <Truck size={14} className="mr-1" /> Deliver to Skyworth
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/jobs/${job.id}`)}
                  className="text-slate-500 hover:text-teal-600">
                  <Eye size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delivered Jobs */}
      <div>
        <h2 className="font-heading font-bold text-sm uppercase tracking-[0.15em] text-slate-700 mb-3 flex items-center gap-2">
          <Truck size={16} className="text-emerald-400" /> Delivered ({deliveredJobs.length})
        </h2>
        {deliveredJobs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400 text-sm">
            No delivered jobs
          </div>
        ) : (
          <div className="space-y-2">
            {deliveredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold text-slate-800">{job.job_card_number}</span>
                    <Badge variant="outline" className={STATUS_COLORS.DELIVERED}>DELIVERED</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {job.model} | {job.store} | {job.area}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/jobs/${job.id}`)}
                  className="text-slate-500 hover:text-teal-600">
                  <Eye size={14} className="mr-1" /> View
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
